import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TrackByFunction, ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BranchDto, SkillDto } from 'src/app/services/api-client/models';
import { SkillRestControllerService } from 'src/app/services/api-client/services';
import { ToastrService } from 'ngx-toastr';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { ActivatedRoute } from '@angular/router';
import { CvTypeEnum } from 'src/app/shared/enums/cvType.model';
import { Subscription } from 'rxjs';
import { Sorting } from 'src/app/shared/sorting';
import { DatePipe } from '@angular/common';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatAccordion } from '@angular/material/expansion';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-skill-data',
  templateUrl: './skill-data.component.html',
  styleUrls: ['./skill-data.component.scss']
})
export class SkillDataComponent implements OnInit, OnChanges, OnDestroy {
  @Output() public skillChangeHandler = new EventEmitter<SkillDto>();
  @Output() public skillsChanged = new EventEmitter<any>();
  @Input() cvId: number = null;
  @Input() cvType: "CONSULTANT_CV" | "ORIGIN_CV" = null;
  @Input() skills: SkillDto = {
    branches: [],
    certificates: [{ name: null, selected: true, certificateDate: null, id: null }],
    id: null,
    itCompetences: [{ id: null, selected: true, name: null, percentageCompetence: 60 }],
    languages: [{ id: null, selected: true, name: null, languageLevel: null }],
    activities: [{ id: null, selected: true, name: null }],
    professionalCompetences: [{ id: null, name: null, selected: true, percentageCompetence: 60 }]
  };
  @ViewChild(MatAccordion) accordion;

  public isConsult = false;

  constructor(
    private sorting: Sorting,
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private skillService: SkillRestControllerService,
    public translate: TranslateService,
    private toastrService: ToastrService,
    private cdr: ChangeDetectorRef
  ) { }

  isEnabled: boolean = true;
  skillsForm: any;
  today = new Date();
  allBranches: BranchDto[] = [];
  selectedBranches: BranchDto[] = [];
  defaultSubscription: Subscription;
  languageLevels: string[] = ['NATIVE', 'ADVANCED', 'INTERMEDIATE', 'BASIC'];

  public dropdownSettings: IDropdownSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'name',
    enableCheckAll: false,
    allowSearchFilter: true,
  };

  ngOnInit(): void {
    this.isConsult = this.isConsultantCV();
    this.isEnabled = !this.isConsult;
    if (!this.skillsForm) this.initializeForm();
    if (this.translate.currentLang == 'en') {
      this.dropdownSettings.textField = 'nameEn';
      this.allBranches.sort((b1, b2) => {return this.sorting.compare(b1.nameEn, b2.nameEn)})
    } else {
      this.allBranches.sort((b1, b2) => {return this.sorting.compare(b1.name, b2.name);})
    }
    // resubscribe because form changed:
    this.defaultSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.allBranches = this.activatedRoute.snapshot.data['branchOffers'];
    let skillChanges: SkillDto = changes['skills']?.currentValue;
    if (skillChanges) {
      this.isEnabled = true;
      this.skills = skillChanges;
      this.skills = skillChanges;
      this.setDataToForm();
    } else {
      this.initializeForm();
    }
    this.defaultSubscription?.unsubscribe();
    this.checkDirty();
    this.cdr.markForCheck();
    // this.defaultSubscription = this.skillsForm.valueChanges.subscribe((change) => this.skillsChanged.emit(change));
  }

  ngOnDestroy(): void {
    if (this.defaultSubscription) {
      this.defaultSubscription.unsubscribe();
    }
  }

  checkDirty(): Subscription {
    return this.skillsForm.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe((change: any) => {
      if (this.skillsForm.dirty || this.skillsForm.touched) {
        this.skillsChanged.emit(change);
      }
    });
  }

  isConsultantCV(): boolean {
    return this.skills && this.cvType === CvTypeEnum.CONSULTANT_CV;
  }

  private getBaseControlGroup(id?: number, name?: string, selected?: boolean): FormGroup {
    return new FormGroup({
      formId: new FormControl(crypto.randomUUID()),
      id: new FormControl(id ? id : null),
      selected: new FormControl((selected === undefined || selected === null) ? true : selected),
      name: new FormControl(name ? name : '', [Validators.compose([
        Validators.required,
        Validators.pattern(/^(\s*\S+\s*)*(?!\s).*$/)
      ])])
    });
  }

  private getPerecentageGroup(id?: number, name?: string, selected?: boolean, perc?: number): FormGroup {
    let baseControl: FormGroup = this.getBaseControlGroup(id, name, selected);
    baseControl.addControl("percentageCompetence", new FormControl(perc ? perc : 0));
    return baseControl;
  }

  private getLanguageGroup(id?: number, name?: string, selected?: boolean, languageLevel?: any): FormGroup {
    let languageGroup = this.getBaseControlGroup(id, name, selected);
    languageGroup.addControl("languageLevel", new FormControl(languageLevel ? languageLevel : '', [Validators.required]));
    return languageGroup;
  }

  handleSelect(i: number, fieldSetName: string, arr: any[]): void {
    const formArr = <FormArray>this.skillsForm.get(fieldSetName);
    let isSelected = !formArr.controls[i].get('selected').value;
    arr?.forEach(x => {
      if(x.name === this.getName(i, fieldSetName)) x.selected = isSelected;
    });
    formArr.controls[i].get('selected').setValue(isSelected);
  }

  private getCertificateGroup(id?: number, name?: string, selected?: boolean, certDate?: string): FormGroup {
    let certificateGroup = this.getBaseControlGroup(id, name, selected);
    certificateGroup.addControl("certificateDate", new FormControl(certDate ? certDate : '', [Validators.required]));
    return certificateGroup;
  }

  private initializeForm() {
    this.skillsForm = this.fb.group({
      languages: new FormArray([this.getLanguageGroup()]),
      certificates: new FormArray([this.getCertificateGroup()]),
      itCompetences: new FormArray([this.getPerecentageGroup()]),
      professionalCompetences: new FormArray([this.getPerecentageGroup()]),
      activities: new FormArray([this.getBaseControlGroup()]),
      branches: new FormControl()
    });
    this.defaultSubscription = this.checkDirty();
  }

  setDataToForm() {
    this.loadSelectedBranch();
    this.skillsForm = new FormGroup({
      languages: new FormArray([]),
      certificates: new FormArray([]),
      itCompetences: new FormArray([]),
      professionalCompetences: new FormArray([]),
      branches: new FormControl(this.selectedBranches),
      activities: new FormArray([])
    });

    setTimeout(() => {
      this.loadFormArray('languages', this.skills.languages);
    }, 500);
    setTimeout(() => {
      this.loadFormArray('certificates', this.skills.certificates);
    }, 500);
    setTimeout(() => {
      this.loadFormArray('itCompetences', this.skills.itCompetences);
    }, 500);
    setTimeout(() => {
      this.loadFormArray('professionalCompetences', this.skills.professionalCompetences);
    }, 500);
    setTimeout(() => {
      this.loadFormArray('activities', this.skills.activities);
    }, 500);
    this.defaultSubscription = this.checkDirty();
  }

  private loadFormArray(fieldSetName: string, items: any[]) {
    const formArray = <FormArray>this.skillsForm.get(fieldSetName);
    items?.forEach((item) => {
      let control = null;
      if (fieldSetName === 'languages') {
        control = this.getLanguageGroup(item.id, item.name, item.selected, item.languageLevel);
      } else if (fieldSetName === 'certificates') {
        control = this.getCertificateGroup(item.id, item.name, item.selected, item.certificateDate);
      } else if (fieldSetName === 'itCompetences' || fieldSetName === 'professionalCompetences') {
        control = this.getPerecentageGroup(item.id, item.name, item.selected, item.percentageCompetence);
      } else {
        control = this.getBaseControlGroup(item.id, item.name, item.selected);
      }
      formArray.push(control);
    });
  }

  loadSelectedBranch() {
    this.selectedBranches = [];
    if (!this.skills.branches) {
      return;
    }
    for (const branch of this.skills.branches) {
      this.selectedBranches.push(this.allBranches.find((b) => b.id === branch.id));
    }
  }

  delete(i: number, fieldSetName: string) {
    this.deleteFromDB(i, fieldSetName);
    const formArray = this.skillsForm.get(fieldSetName) as FormArray;
    if (i >= 0 && i < formArray.length) {
      formArray.removeAt(i);
      this.checkFieldSet(fieldSetName, this.skillsForm.get(fieldSetName).length);
      this.skillsChanged.emit(true);
      this.cdr.detectChanges();
    } else {
      console.warn(`Index ${i} is out of bounds for formArray ${fieldSetName}`);
    }
  }

  deleteFromDB(i: number, fieldSetName: string) {
    const arr = <FormArray>this.skillsForm.get(fieldSetName);
    if (arr.controls[i].get('id').value != null && arr.controls[i].get('id').value != '') {
      this.removeFromArray(arr.controls[i].get('id').value, fieldSetName);
    }
  }

  removeFromArray(key, fieldSetName: string) {
    switch (fieldSetName) {
      case 'languages':
        this.skills.languages.forEach((language, index) => {
          if (language.id == key) this.skills.languages.splice(index, 1);
        });
        break;
      case 'certificates':
        this.skills.certificates.forEach((cert, index) => {
          if (cert.id == key) this.skills.certificates.splice(index, 1);
        });
        break;
      case 'itCompetences':
        this.skills.itCompetences.forEach((it, index) => {
          if (it.id == key) this.skills.itCompetences.splice(index, 1);
        });
        break;
      case 'professionalCompetences':
        this.skills.professionalCompetences.forEach((prof, index) => {
          if (prof.id == key) this.skills.professionalCompetences.splice(index, 1);
        });
        break;
      case 'activities':
        this.skills.activities.forEach((activity, index) => {
          if (activity.id == key) this.skills.activities.splice(index, 1);
        });
        break;
    }
  }

  checkFieldSet(setName: string, length: number) {
    if (length == 0) {
      this.addNewItem(setName);
    }
  }

  addNewItem(fieldSetName: string) {
    const fields: FormArray = <FormArray>this.skillsForm.get(fieldSetName);
    let newItem;

    switch (fieldSetName) {
      case 'languages':
        newItem = this.getLanguageGroup(null, '', true, '');
        this.skills.languages.push({ id: null, selected: true, name: null, languageLevel: null });
        if (!this.skills.languages) {
          this.skills.languages = [];
        }
        break;
      case 'certificates':
        newItem = this.getCertificateGroup(null, '', true, '');
        if (!this.skills.certificates) {
          this.skills.certificates = [];
        }
        this.skills.certificates.push({ name: null, selected: true, certificateDate: null, id: null });
        break;
      case 'itCompetences':
        newItem = this.getPerecentageGroup(null, '', true, 60);
        if (!this.skills.itCompetences) {
          this.skills.itCompetences = [];
        }
        this.skills.itCompetences.push({ id: null, selected: true, name: null, percentageCompetence: 60 });
        break;
      case 'professionalCompetences':
        newItem = this.getPerecentageGroup(null, '', true, 60);
        if (!this.skills.professionalCompetences) {
          this.skills.professionalCompetences = [];
        }
        this.skills.professionalCompetences.push({ id: null, selected: true, name: null, percentageCompetence: 60 });
        break;
      case 'activities':
        newItem = this.getBaseControlGroup(null, '', true);
        if (!this.skills.activities) {
          this.skills.activities = [];
        }
        this.skills.activities.push({ id: null, selected: true, name: null });
        break;
    }

    fields.push(newItem);
    this.skillsForm.updateValueAndValidity();
  }

  trackByIndex: TrackByFunction<FormControl> = (index, v) => {
    if (v?.value?.formId) {
      return v.value.formId;
    }
    if (v?.value?.id) {
      return v.value.id;
    }
    if (v?.value?.name) {
      return v.value.name;
    }
    return index;
  };

  toggleSkills(): void {
    this.isEnabled = !this.isEnabled;
    this.isEnabled ? this.accordion.openAll() : this.accordion.closeAll();
  }

  itemDropped(event: CdkDragDrop<string[]>, fieldSetName: string) {
    let objArr = this.skillsForm.get(fieldSetName).value;
    this.swap(event.previousIndex, event.currentIndex, objArr);
    if (fieldSetName != 'branches') {
      this.swap(event.previousIndex, event.currentIndex, this.skillsForm.get(fieldSetName).controls)
    }
    this.setArray(fieldSetName, objArr);
  }

  onOptionsSelected(value: string) {
    const langArray = <FormArray>this.skillsForm.get('languages');
    langArray.controls[(this.skillsForm.get('languages') as FormArray).length - 1].get('name').setValue(value);
  }

  onItemSelect(branch: BranchDto) {
      this.skills?.branches?.push(branch);
  }

  onItemDeSelect(branch: BranchDto) {
      this.skills?.branches?.forEach((b, index ) => {
        if (b.id === branch.id)
          this.skills.branches.splice(index, 1);
      })
  }

  onSelectAll(items: any) {
  }

  isItemSelected(i: number, fieldSetName: string): boolean {
    return this.skillsForm.get(fieldSetName).controls[i].get('selected').value;
  }

  getName(i: number, fieldSetName: string): string {
    return this.skillsForm.get(fieldSetName).controls[i].get('name').value;
  }

  setArray(fieldSetName: string, objArr: any[]) {
    switch (fieldSetName) {
      case 'languages': this.skills.languages = objArr; break;
      case 'branches': this.skills.branches = objArr; break;
      case 'certificates': this.skills.certificates = objArr; break;
      case 'itCompetences': this.skills.itCompetences = objArr; break;
      case 'professionalCompetences': this.skills.professionalCompetences = objArr; break;
      case 'activities': this.skills.activities = objArr; break;
    }
  }

  private swap(previousIndex: number, currentIndex: number, array: any[]) {
    moveItemInArray(array, previousIndex, currentIndex);
    this.skillsChanged.emit(true);
  }

  onSubmit() {
    if (this.skillsForm.valid) {
      const newSkillData: SkillDto = this.skillsForm.value;
      if (this.skills?.id === this.cvId) {
        newSkillData.id = this.skills.id;
      }
      // Branches need to have all values
      let fullBranchList: BranchDto[] = [];
      if (newSkillData.branches) {
        newSkillData.branches.forEach((branch) =>
          fullBranchList.push(this.allBranches.find((allBranch) => allBranch.id === branch.id))
        );
      }
      newSkillData.branches = fullBranchList;

      const datepipe: DatePipe = new DatePipe('en-US');
      if (newSkillData.certificates) {
        newSkillData.certificates.forEach(certificate => {
          let formattedDate = datepipe.transform(new Date(certificate.certificateDate), 'yyyy-MM-dd');
          certificate.certificateDate = formattedDate;
        })
      }

      this.skillService.createOrUpdateSkill({ cvId: this.cvId, body: newSkillData }).subscribe(
        (response: any) => {
          this.skills = response;
          this.setDataToForm();
          this.skillsChanged.emit(false);
          this.skillsForm.markAsPristine();
          this.translate.get('saved_skills_data').subscribe(message => {
            this.toastrService.success(message);
          });
          this.isConsult = this.isConsultantCV();
        }, (e) => {
          console.log('Error! Failed to save data', e);
          this.toastrService.error("Ein unerwarteter Fehler ist aufgetreten.")
        });
    }
  }

  public findInvalidControls() {
    const invalid = [];
    const controls = this.skillsForm.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name);
      }
    }
    return invalid;
  }

}
